import { debounce } from 'throttle-debounce';

const selector = '.relatedArticles[data-side-scroll]';
const headerSelector = '.relatedArticles-header';
const headingSelector = '.relatedArticles-heading';
const listSelector = '.relatedArticles-list';
const listContentSelector = '.relatedArticles-listContent';
const listContainerSelector = '.relatedArticles-listContainer';
const scrollButtonSelector = '.relatedArticles-scrollButton';
const scrollButtonLabelSelector = '.relatedArticles-scrollButtonLabel';
const scrollTolerance = 4;
const reducedMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

let elementIdCount = 0;

const createElementId = prefix => {
    elementIdCount += 1;
    return `${prefix}-${elementIdCount}`;
};

const ensureElementId = (element, prefix) => {
    if (!element.id) {
        element.id = createElementId(prefix);
    }

    return element.id;
};

const getScrollButtonLabels = () => {
    const documentLanguage = document.documentElement.getAttribute('lang') || '';
    const isEnglish = documentLanguage.toLowerCase().indexOf('en') === 0;

    return {
        next: isEnglish ? 'Show more' : 'Se flere',
        back: isEnglish ? 'Back to start' : 'Tilbake til start',
        nextWithoutHeading: isEnglish ? 'Show more related content' : 'Se mer relatert innhold',
        backWithoutHeading: isEnglish ? 'Back to start of related content' : 'Tilbake til starten av relatert innhold'
    };
};

const getPixelValue = value => {
    const parsedValue = parseFloat(value);

    return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const getHorizontalOverflowAllowance = list => {
    const lastListItem = list.lastElementChild;

    if (!(lastListItem instanceof HTMLElement)) {
        return 0;
    }

    return getPixelValue(window.getComputedStyle(lastListItem).paddingRight);
};

const hasHorizontalScroll = (related, listContainer, list) => {
    related.classList.add('relatedArticles--horizontal');

    const scrollOverflow = listContainer.scrollWidth - listContainer.clientWidth - getHorizontalOverflowAllowance(list);
    const isHorizontal = Math.ceil(scrollOverflow) > scrollTolerance;
    related.classList.toggle('relatedArticles--horizontal', isHorizontal);

    return isHorizontal;
};

const getMaxScrollLeft = listContainer => Math.max(listContainer.scrollWidth - listContainer.clientWidth, 0);

const clampScrollLeft = (listContainer, position) => Math.max(0, Math.min(position, getMaxScrollLeft(listContainer)));

const setListScrollPosition = (listContainer, position = 0) => {
    listContainer.scrollLeft = clampScrollLeft(listContainer, position);
};

const scrollListToPosition = (listContainer, position) => {
    const left = clampScrollLeft(listContainer, position);

    if (typeof listContainer.scrollTo === 'function') {
        try {
            listContainer.scrollTo({
                left,
                behavior: reducedMotionMediaQuery.matches ? 'auto' : 'smooth'
            });
            return;
        } catch (error) {
            // nothing
        }
    }

    setListScrollPosition(listContainer, left);
};

const getListItemStart = (listContainer, listItem) => {
    const listContainerRect = listContainer.getBoundingClientRect();
    const listItemRect = listItem.getBoundingClientRect();

    return clampScrollLeft(listContainer, listContainer.scrollLeft + listItemRect.left - listContainerRect.left);
};

const getSnapPositions = (listContainer, list) => Array.from(list.children).reduce((positions, listItem) => {
    const position = getListItemStart(listContainer, listItem);

    if (!positions.some(existingPosition => Math.abs(existingPosition - position) <= scrollTolerance)) {
        positions.push(position);
    }

    return positions;
}, []);

const getActiveSnapIndex = (snapPositions, scrollLeft) => snapPositions.reduce((closestIndex, position, index, positions) => {
    if (Math.abs(position - scrollLeft) < Math.abs(positions[closestIndex] - scrollLeft)) {
        return index;
    }

    return closestIndex;
}, 0);

const isScrolledToEnd = listContainer => {
    const maxScrollLeft = getMaxScrollLeft(listContainer);

    return maxScrollLeft > 0 && listContainer.scrollLeft >= maxScrollLeft - scrollTolerance;
};

const ensureScrollButtonLabel = scrollButton => {
    let scrollButtonLabel = scrollButton.querySelector(scrollButtonLabelSelector);

    if (!scrollButtonLabel) {
        scrollButtonLabel = document.createElement('span');
        scrollButtonLabel.classList.add('relatedArticles-scrollButtonLabel', 'sr-only');
        scrollButton.appendChild(scrollButtonLabel);
    }

    ensureElementId(scrollButtonLabel, 'related-articles-scroll-button-label');

    return scrollButtonLabel;
};

const updateScrollButtonLabel = (scrollButton, heading, isBack) => {
    const labels = getScrollButtonLabels();
    const scrollButtonLabel = ensureScrollButtonLabel(scrollButton);

    scrollButtonLabel.textContent = heading
        ? (isBack ? labels.back : labels.next)
        : (isBack ? labels.backWithoutHeading : labels.nextWithoutHeading);

    if (heading) {
        scrollButton.setAttribute('aria-labelledby', `${scrollButtonLabel.id} ${ensureElementId(heading, 'related-articles-heading')}`);
        scrollButton.removeAttribute('aria-label');
        return;
    }

    scrollButton.removeAttribute('aria-labelledby');
    scrollButton.setAttribute('aria-label', scrollButtonLabel.textContent);
};

const setScrollButtonState = (scrollButton, heading, isBack) => {
    scrollButton.classList.toggle('relatedArticles-scrollButton--back', isBack);
    updateScrollButtonLabel(scrollButton, heading, isBack);
};

const updateScrollButtonState = (scrollButton, heading, listContainer, list) => {
    const snapPositions = getSnapPositions(listContainer, list);
    const activeSnapIndex = snapPositions.length > 0 ? getActiveSnapIndex(snapPositions, listContainer.scrollLeft) : 0;
    const isBack = snapPositions.length > 1 && (activeSnapIndex >= snapPositions.length - 1 || isScrolledToEnd(listContainer));

    setScrollButtonState(scrollButton, heading, isBack);
};

const ensureHeaderContainer = (related, heading) => {
    let header = related.querySelector(headerSelector);

    if (!header) {
        header = document.createElement('div');
        header.classList.add('relatedArticles-header');

        const listContent = related.querySelector(listContentSelector);

        if (listContent) {
            related.insertBefore(header, listContent);
        } else {
            related.appendChild(header);
        }
    }

    if (heading && heading.parentNode !== header) {
        header.insertBefore(heading, header.firstChild);
    }

    return header;
};

const createOrGetScrollButton = (related, listContainer, list, heading) => {
    const header = ensureHeaderContainer(related, heading);
    let scrollButton = related.querySelector(scrollButtonSelector);

    if (!scrollButton) {
        scrollButton = document.createElement('button');
        scrollButton.type = 'button';
        scrollButton.classList.add('relatedArticles-scrollButton');

        scrollButton.addEventListener('click', event => {
            const button = event.currentTarget;
            const snapPositions = getSnapPositions(listContainer, list);

            if (snapPositions.length === 0) {
                return;
            }

            if (button.classList.contains('relatedArticles-scrollButton--back')) {
                scrollListToPosition(listContainer, 0);
                setScrollButtonState(button, heading, false);
                return;
            }

            const currentSnapIndex = getActiveSnapIndex(snapPositions, listContainer.scrollLeft);
            const targetSnapIndex = Math.min(currentSnapIndex + 1, snapPositions.length - 1);
            const targetPosition = snapPositions[targetSnapIndex];
            const shouldFlipToBack = targetSnapIndex >= snapPositions.length - 1 || targetPosition >= getMaxScrollLeft(listContainer) - scrollTolerance;

            scrollListToPosition(listContainer, targetPosition);
            setScrollButtonState(button, heading, shouldFlipToBack);
        });

        let scrollUpdateFrame = null;

        listContainer.addEventListener('scroll', () => {
            if (scrollUpdateFrame !== null) {
                cancelAnimationFrame(scrollUpdateFrame);
            }

            scrollUpdateFrame = requestAnimationFrame(() => {
                updateScrollButtonState(scrollButton, heading, listContainer, list);
                scrollUpdateFrame = null;
            });
        }, { passive: true });

        header.appendChild(scrollButton);
    } else if (scrollButton.parentNode !== header) {
        header.appendChild(scrollButton);
    }

    ensureScrollButtonLabel(scrollButton);
    scrollButton.setAttribute('aria-controls', ensureElementId(listContainer, 'related-articles-list-container'));
    updateScrollButtonState(scrollButton, heading, listContainer, list);

    return scrollButton;
};

const updateSideScroll = resetPosition => {
    const relatedArticles = document.querySelectorAll(selector);

    relatedArticles.forEach(related => {
        const listContainer = related.querySelector(listContainerSelector);
        const list = related.querySelector(listSelector);

        if (!(listContainer && list && list.children.length >= 2)) {
            return;
        }

        const heading = related.querySelector(headingSelector);
        const scrollButton = createOrGetScrollButton(related, listContainer, list, heading);
        const isHorizontal = hasHorizontalScroll(related, listContainer, list);
        const shouldShowButton = isHorizontal;

        if (resetPosition || !isHorizontal) {
            setListScrollPosition(listContainer);
        }

        scrollButton.hidden = !shouldShowButton;
        scrollButton.disabled = !shouldShowButton;

        updateScrollButtonState(scrollButton, heading, listContainer, list);
    });
};

const resetSideScroll = () => {
    updateSideScroll(true);
};

const setupSideScroll = () => {
    updateSideScroll(true);
};

window.addEventListener('resize', debounce(300, resetSideScroll));

if (document.readyState !== 'complete') {
    window.addEventListener('load', setupSideScroll, false);
} else {
    setupSideScroll();
}
